import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Folder,
  Image,
  VolumeUp,
  Close,
  AutoAwesome,
  PlayArrow,
  Stop,
  Refresh
} from '@mui/icons-material';
import { Category, CategoryFormData } from '../types/category';
import { Source } from '../types/source';
import categoryService from '../services/categoryService';
import sourceService from '../services/sourceService';
import aiService from '../services/aiService';

// Normalize imageContentType to a valid MIME type (e.g. "png" → "image/png")
const normalizeImageMime = (contentType?: string | null) => {
  if (!contentType) return 'image/png';
  return contentType.startsWith('image/') ? contentType : `image/${contentType}`;
};

export const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [formData, setFormData] = useState<CategoryFormData>({
    code: '',
    name: '',
    description: '',
    parentCode: '',
    audioText: '',
    source: 1, // Default to "System"
    status: 1
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [audioPreview, setAudioPreview] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{url: string, name: string} | null>(null);
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
    loadSources();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await categoryService.getCategories();
      setCategories(data);
    } catch (err: any) {
      setError('Failed to load categories: ' + (err.message || 'Unknown error'));
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSources = async () => {
    try {
      console.log('🔍 Loading sources...');
      const data = await sourceService.getSources();
      console.log('📦 Raw sources data:', data);
      // Only use active sources (status === 1)
      const activeSources = data.filter((source: any) => source.status === 1);
      console.log('✅ Active sources:', activeSources);
      setSources(activeSources);
    } catch (err: any) {
      console.error('❌ Error loading sources:', err);
      // Don't show error to user as sources are not critical for basic functionality
    }
  };

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.code.trim()) {
      errors.code = 'Code is required';
    }
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    // Require audio text for new categories
    if (!selectedCategory && (!formData.audioText || !formData.audioText.trim())) {
      errors.audioText = 'Audio text is required for new categories';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      parentCode: '',
      audioText: '',
      source: 1, // Default to "System"
      status: 1
    });
    setFormErrors({});
    setImagePreview(null);
    setAudioPreview(null);
    if (audioPlayer) {
      audioPlayer.pause();
      setAudioPlayer(null);
    }
    setPlayingAudio(null);
  };

  // Handle opening image in modal
  const handleAudioPreview = (audioUrl: string, categoryId?: string) => {
    if (playingAudio === (categoryId || 'preview')) {
      // Stop current audio
      if (audioPlayer) {
        audioPlayer.pause();
        setAudioPlayer(null);
      }
      setPlayingAudio(null);
    } else {
      // Stop any currently playing audio
      if (audioPlayer) {
        audioPlayer.pause();
      }
      
      // Play new audio
      const audio = new Audio(audioUrl);
      audio.play();
      audio.onended = () => {
        setPlayingAudio(null);
        setAudioPlayer(null);
      };
      setAudioPlayer(audio);
      setPlayingAudio(categoryId || 'preview');
    }
  };

  // Handle opening image in modal
  const handleImagePreview = (imageUrl: string, categoryName: string) => {
    setSelectedImage({ url: imageUrl, name: categoryName });
    setImageModalOpen(true);
  };

  // Generate audio for category
  const handleGenerateAudio = async (categoryId?: string, audioText?: string) => {
    const textToGenerate = audioText || formData.audioText || formData.name;
    
    try {
      setGeneratingAudio(true);
      
      let result;
      if (categoryId) {
        // Generate for existing category using GraphQL mutation by ID
        result = await categoryService.generateAudio(categoryId, textToGenerate);
      } else if (selectedCategory) {
        // Generate for category being edited using GraphQL mutation by ID
        result = await categoryService.generateAudio(selectedCategory.id, textToGenerate);
      } else {
        // For new categories, generate audio for all voices atomically (all-or-nothing)
        const { results: voiceResults, errorMessage } = await aiService.generateAudioAllVoices(textToGenerate);
        if (errorMessage) {
          setError(errorMessage);
          return;
        }
        if (voiceResults.length > 0 && voiceResults[0].audioBase64) {
          setAudioPreview(`data:audio/mpeg;base64,${voiceResults[0].audioBase64}`);
        }
        result = { success: voiceResults.length > 0, message: voiceResults.length > 0 ? 'Audio generated for all voices' : 'No audio generated' };
      }
      
      if (result.success) {
        if (categoryId || selectedCategory) {
          // Update audio preview immediately from the result
          if (result.audioUrl) {
            console.log('🔊 Setting audio preview from result:', result.audioUrl.substring(0, 50) + '...');
            setAudioPreview(result.audioUrl);
          }
          
          // Reload categories to get updated data in the background
          loadCategories().catch(console.error);
          
          // If we're in edit mode, update the selectedCategory with the new audio data
          if (selectedCategory && result.audioUrl) {
            // Create a mock sound option for the updated category
            const mockSoundOption = {
              voiceId: 'generated',
              soundData: result.audioUrl.split(',')[1], // Extract base64 part
              soundFileName: `category_${selectedCategory.code}_generated.mp3`,
              soundContentType: 'audio/mpeg',
              soundGenerated: true,
              soundGeneratedAt: new Date().toISOString()
            };
            
            setSelectedCategory({
              ...selectedCategory,
              soundOptions: [mockSoundOption, ...(selectedCategory.soundOptions || [])]
            });
          }
          
          showSnackbar('Audio generated and saved successfully');
        } else {
          showSnackbar('Audio generated successfully. Save the category to apply changes.');
        }
      } else {
        setError(result.message || 'Failed to generate audio');
      }
      
    } catch (err: any) {
      console.error('Error generating audio:', err);
      setError('Failed to generate audio: ' + (err.message || 'Unknown error'));
    } finally {
      setGeneratingAudio(false);
    }
  };

  // Generate image for category
  const handleGenerateImage = async (categoryId?: string, prompt?: string) => {
    const imagePrompt = prompt || `A simple, colorful icon representing ${formData.name}`;
    
    try {
      setGeneratingImage(true);
      
      let result;
      if (categoryId) {
        // Generate for existing category using GraphQL
        result = await categoryService.generateImage(categoryId, imagePrompt);
      } else if (selectedCategory) {
        // Generate for category being edited
        result = await categoryService.generateImage(selectedCategory.id, imagePrompt);
      } else {
        // For new categories, generate image preview using AI service
        result = await aiService.generateImage({
          prompt: imagePrompt,
          size: '1024x1024'
        });
        
        // Set the image preview if successful
        if (result.success && result.imageUrl) {
          // Convert the image URL to base64 for preview
          try {
            const response = await fetch(result.imageUrl);
            const blob = await response.blob();
            const reader = new FileReader();
            reader.onloadend = () => {
              setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(blob);
          } catch (fetchErr) {
            console.warn('Could not convert image to base64, using URL directly:', fetchErr);
            setImagePreview(result.imageUrl);
          }
        }
      }
      
      if (result.success) {
        if (categoryId || selectedCategory) {
          // Update image preview immediately from the result
          if (result.imageUrl) {
            console.log('🖼️ Setting image preview from result:', result.imageUrl.substring(0, 50) + '...');
            setImagePreview(result.imageUrl);
          }
          
          // Reload categories to get updated data in the background
          loadCategories().catch(console.error);
          
          // If we're in edit mode, update the selectedCategory with the new image data
          if (selectedCategory) {
            setSelectedCategory({
              ...selectedCategory,
              imageData: result.imageUrl ? result.imageUrl.split(',')[1] : selectedCategory.imageData, // Extract base64 part
              imageContentType: 'image/png',
              imageGenerated: true,
              imageGeneratedAt: new Date().toISOString()
            });
          }
          
          showSnackbar('Image generated and saved successfully');
        } else {
          showSnackbar('Image preview generated successfully. Save the category to apply changes.');
        }
      } else {
        setError(result.message || 'Failed to generate image');
      }
      
    } catch (err: any) {
      console.error('Error generating image:', err);
      setError('Failed to generate image: ' + (err.message || 'Unknown error'));
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleAddCategory = () => {
    resetForm();
    setSelectedCategory(null);
    setOpenDialog(true);
  };

  const handleEditCategory = (category: Category) => {
    setFormData({
      code: category.code,
      name: category.name,
      description: category.description || '',
      parentCode: category.parentCode || '',
      audioText: category.audioText || '',
      source: category.source,
      status: category.status
    });
    setSelectedCategory(category);
    
    // Set up preview URLs if the category has existing media
    if (category.imageData) {
      setImagePreview(`data:${normalizeImageMime(category.imageContentType)};base64,${category.imageData}`);
    }
    // For sound, use the first available sound option
    if (category.soundOptions?.[0]?.soundData) {
      setAudioPreview(`data:${category.soundOptions[0].soundContentType || 'audio/mpeg'};base64,${category.soundOptions[0].soundData}`);
    }
    
    setOpenDialog(true);
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        setLoading(true);
        await categoryService.deleteCategory(categoryId);
        await loadCategories();
        showSnackbar('Category deleted successfully');
      } catch (err: any) {
        setError('Failed to delete category: ' + (err.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleStatus = async (categoryId: string, currentStatus: number) => {
    try {
      setLoading(true);
      const newStatus = currentStatus === 1 ? 0 : 1;
      await categoryService.toggleCategoryStatus(categoryId, newStatus);
      await loadCategories();
      showSnackbar(`Category ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
    } catch (err: any) {
      setError('Failed to update category status: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
    setSelectedCategory(null);
  };

  const handleSaveCategory = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const categoryData: any = {
        code: formData.code,
        name: formData.name,
        description: formData.description || undefined,
        parentCode: formData.parentCode || '',
        audioText: formData.audioText || undefined,
        source: formData.source,
        status: formData.status
      };

      // For updates, preserve existing image and audio data
      if (selectedCategory) {
        // Preserve existing image data
        if (selectedCategory.imageData) {
          categoryData.imageData = selectedCategory.imageData;
          categoryData.imageFileName = selectedCategory.imageFileName;
          categoryData.imageContentType = selectedCategory.imageContentType;
          categoryData.imageGenerated = selectedCategory.imageGenerated;
          // Ensure imageGeneratedAt is set (for backwards compatibility with older categories)
          categoryData.imageGeneratedAt = selectedCategory.imageGeneratedAt || new Date().toISOString();
        }

        // Preserve existing sound options (clean Apollo client fields)
        if (selectedCategory.soundOptions && selectedCategory.soundOptions.length > 0) {
          categoryData.soundOptions = selectedCategory.soundOptions.map(option => ({
            voiceId: option.voiceId,
            soundData: option.soundData,
            soundFileName: option.soundFileName,
            soundContentType: option.soundContentType,
            soundGenerated: option.soundGenerated,
            soundGeneratedAt: option.soundGeneratedAt
          }));
        }
      }

      if (selectedCategory) {
        // Update existing category
        
        // Check if audio text has changed to determine if we need to regenerate audio
        const audioTextChanged = formData.audioText !== selectedCategory.audioText;
        
        if (audioTextChanged && formData.audioText) {
          console.log('Audio text changed, will regenerate audio after update');
          // Don't preserve old sound options - we'll regenerate them
          await categoryService.updateCategory(selectedCategory.id, categoryData);
          
          // Regenerate audio with the new text
          try {
            const audioResult = await categoryService.generateAudio(
              selectedCategory.code, 
              formData.audioText
            );
            
            if (audioResult.success) {
              showSnackbar('Category updated successfully with new audio generated');
            } else {
              showSnackbar(`Category updated, but audio generation failed: ${audioResult.message}`);
            }
          } catch (audioError) {
            console.error('Error generating new audio:', audioError);
            showSnackbar('Category updated, but audio generation failed');
          }
        } else {
          // Audio text hasn't changed, just update normally
          await categoryService.updateCategory(selectedCategory.id, categoryData);
          showSnackbar('Category updated successfully');
        }
        
        await loadCategories();
        handleCloseDialog();
      } else {
        // Create new category (or reactivate existing one with same code)
        const newCategory = await categoryService.createCategory(categoryData);
        
        // Use metadata from service to determine appropriate message
        if (newCategory._wasReactivated) {
          showSnackbar(`Category with code "${categoryData.code}" already existed and has been reactivated with updated information`);
        } else {
          showSnackbar('Category created successfully with image and audio generated automatically');
        }
        
        // Reload categories and keep the dialog open for media generation
        await loadCategories();
        
        // Set the newly created category as selected to enable media generation
        setSelectedCategory(newCategory);
        
        // Update form data with the created category data
        setFormData({
          ...formData,
          code: newCategory.code,
          name: newCategory.name,
          description: newCategory.description || '',
          audioText: newCategory.audioText || '',
          source: newCategory.source,
          status: newCategory.status
        });
      }
    } catch (err: any) {
      setError('Failed to save category: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };



  const filteredCategories = categories.filter(category => {
    // Text search filter
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && category.status === 1) ||
      (statusFilter === 'inactive' && category.status === 0);
    
    return matchesSearch && matchesStatus;
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: number) => {
    return status === 1 ? 'success' : 'default';
  };

  const getStatusText = (status: number) => {
    return status === 1 ? 'Active' : 'Inactive';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            placeholder="Search categories..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0); // Reset to first page when search changes
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                setPage(0); // Reset to first page when filter changes
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          {(searchTerm || statusFilter !== 'all') && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setPage(0);
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddCategory}
          disabled={loading}
        >
          Add Category
        </Button>
      </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: 'primary.light',
                      color: 'primary.main',
                      mr: 2
                    }}
                  >
                    <Folder />
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {categories.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Categories
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: 'success.light',
                      color: 'success.main',
                      mr: 2
                    }}
                  >
                    <Folder />
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {categories.filter(c => c.status === 1).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Categories
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: 'error.light',
                      color: 'error.main',
                      mr: 2
                    }}
                  >
                    <Folder />
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {categories.filter(c => c.status === 0).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Inactive Categories
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: 'warning.light',
                      color: 'warning.main',
                      mr: 2
                    }}
                  >
                    <VolumeUp />
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {categories.filter(c => 
                        c.soundOptions && c.soundOptions.length > 0 && c.soundOptions.some(option => option.soundGenerated)
                      ).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      With Audio
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: 'info.light',
                      color: 'info.main',
                      mr: 2
                    }}
                  >
                    <Image />
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {categories.filter(c => c.imageGenerated && c.imageData).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      With Images
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Categories Table */}
        <Paper elevation={2}>
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table sx={{ 
                    '& th, & td': { 
                      verticalAlign: 'top',
                      padding: '12px 16px'
                    },
                    '& th': { 
                      backgroundColor: 'grey.50',
                      fontWeight: 600
                    },
                    '& tbody tr:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '100px' }}>Code</TableCell>
                        <TableCell sx={{ minWidth: '150px' }}>Name</TableCell>
                        <TableCell sx={{ maxWidth: '200px' }}>Description</TableCell>
                        <TableCell sx={{ width: '80px' }}>Image</TableCell>
                        <TableCell sx={{ width: '100px' }}>Status</TableCell>
                        <TableCell sx={{ width: '180px' }}>Assets</TableCell>
                        <TableCell sx={{ width: '100px' }}>Created</TableCell>
                        <TableCell align="right" sx={{ width: '150px' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCategories
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((category) => (
                          <TableRow key={category.id} hover>
                            <TableCell sx={{ verticalAlign: 'top', width: '100px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                {category.code}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', minWidth: '150px' }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 600,
                                  wordBreak: 'break-word',
                                  hyphens: 'auto'
                                }}
                              >
                                {category.name}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', maxWidth: '200px' }}>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                  wordBreak: 'break-word',
                                  hyphens: 'auto'
                                }}
                              >
                                {category.description || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              {category.imageGenerated && category.imageData ? (
                                <Box
                                  component="img"
                                  src={`data:${normalizeImageMime(category.imageContentType)};base64,${category.imageData}`}
                                  alt={category.name}
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                      transform: 'scale(1.1)',
                                    }
                                  }}
                                  onClick={() => handleImagePreview(
                                    `data:${normalizeImageMime(category.imageContentType)};base64,${category.imageData}`,
                                    category.name
                                  )}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'grey.100',
                                    borderRadius: 1,
                                    border: '1px dashed',
                                    borderColor: 'grey.300'
                                  }}
                                >
                                  <Image sx={{ color: 'grey.400', fontSize: 24 }} />
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <Chip
                                label={getStatusText(category.status)}
                                color={getStatusColor(category.status) as any}
                                size="small"
                                onClick={() => handleToggleStatus(category.id, category.status)}
                                sx={{ cursor: 'pointer' }}
                              />
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                                {category.imageGenerated ? (
                                  <Chip icon={<Image />} label="Image" size="small" variant="outlined" color="info" />
                                ) : (
                                  <Chip icon={<Image />} label="No Image" size="small" variant="outlined" color="default" />
                                )}
                                {/* Simple audio check - if soundData exists, show Audio + play button */}
                                {category.soundOptions?.[0]?.soundData ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Chip icon={<VolumeUp />} label="Audio" size="small" variant="outlined" color="warning" />
                                    <IconButton size="small" onClick={() => {
                                      const soundOption = category.soundOptions?.[0];
                                      if (soundOption?.soundData) {
                                        handleAudioPreview(
                                          `data:${soundOption.soundContentType || 'audio/mpeg'};base64,${soundOption.soundData}`,
                                          category.id
                                        );
                                      }
                                    }} sx={{ ml: 0.5 }}>
                                      {playingAudio === category.id ? <Stop /> : <PlayArrow />}
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Chip icon={<VolumeUp />} label="No Audio" size="small" variant="outlined" color="default" />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(category.createdDate)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditCategory(category)}
                                  disabled={loading}
                                  title="Edit category"
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleGenerateImage(category.id)}
                                  disabled={loading || generatingImage}
                                  title="Generate image"
                                  color="info"
                                >
                                  {generatingImage ? <CircularProgress size={16} /> : <AutoAwesome />}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleGenerateAudio(category.id, category.audioText || category.name)}
                                  disabled={loading || generatingAudio || !(category.audioText?.trim() || category.name?.trim())}
                                  title={!(category.audioText?.trim() || category.name?.trim()) ? "No text available for audio generation" : "Generate audio"}
                                  color="warning"
                                >
                                  {generatingAudio ? <CircularProgress size={16} /> : <AutoAwesome />}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteCategory(category.id)}
                                  disabled={loading}
                                  color="error"
                                  title="Delete category"
                                >
                                  <Delete />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredCategories.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </Box>
        </Paper>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {selectedCategory 
                  ? `Edit Category: ${selectedCategory.name}` 
                  : 'Add New Category'
                }
              </Typography>
              {selectedCategory && (
                <Typography variant="caption" color="text.secondary">
                  Add images and audio to enhance your category
                </Typography>
              )}
              <IconButton onClick={handleCloseDialog}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Show info alert when category was just created */}
              {selectedCategory && selectedCategory.code === formData.code && 
               selectedCategory.name === formData.name && !error && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Category created successfully! You can now add images and audio below.
                </Alert>
              )}
              
              {/* Basic Information */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    error={!!formErrors.code}
                    helperText={formErrors.code}
                    required
                    fullWidth
                    disabled={!!selectedCategory} // Don't allow changing code when editing
                  />
                  <FormControl fullWidth>
                    <InputLabel>Parent Category</InputLabel>
                    <Select
                      value={formData.parentCode || ''}
                      label="Parent Category"
                      onChange={(e) => setFormData({ ...formData, parentCode: e.target.value || '' })}
                    >
                      <MenuItem value="">
                        <em>None (Top Level)</em>
                      </MenuItem>
                      {categories
                        .filter(cat => cat.status === 1 && cat.code !== formData.code) // Only show active categories, exclude self
                        .map((category) => (
                          <MenuItem key={category.id} value={category.code}>
                            {category.name} ({category.code})
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    multiline
                    rows={3}
                    fullWidth
                  />
                  <TextField
                    required={!selectedCategory} // Required for new categories
                    label="Audio Text"
                    value={formData.audioText}
                    onChange={(e) => setFormData({ ...formData, audioText: e.target.value })}
                    helperText={formErrors.audioText || "Text to be converted to speech"}
                    error={!!formErrors.audioText}
                    fullWidth
                  />
                  <FormControl fullWidth>
                    <InputLabel>Source</InputLabel>
                    <Select
                      value={formData.source}
                      label="Source"
                      onChange={(e) => setFormData({ ...formData, source: e.target.value as number })}
                    >
                      {sources.map((source) => (
                        <MenuItem key={source.id} value={source.sourceId}>
                          {source.name}
                        </MenuItem>
                      ))}
                      {sources.length === 0 && (
                        <MenuItem value={1}>System</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as number })}
                    >
                      <MenuItem value={1}>Active</MenuItem>
                      <MenuItem value={0}>Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Image Management */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>Image</Typography>
                
                {/* Image state indicators */}
                {!selectedCategory && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Image will be automatically generated when you create the category.
                  </Alert>
                )}
                {imagePreview && !selectedCategory && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Image preview generated. Click Save (Update) to persist image to database.
                  </Alert>
                )}
                {selectedCategory && selectedCategory.imageData && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Image has been saved to the database for this category.
                  </Alert>
                )}
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(imagePreview || (selectedCategory && selectedCategory.imageData)) && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <img
                        src={imagePreview || (selectedCategory?.imageData ? `data:${normalizeImageMime(selectedCategory.imageContentType)};base64,${selectedCategory.imageData}` : '')}
                        alt="Preview"
                        style={{
                          maxWidth: '200px',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          border: '1px solid #ccc',
                          borderRadius: '8px'
                        }}
                      />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<AutoAwesome />}
                      onClick={() => handleGenerateImage()}
                      disabled={generatingImage || !formData.name.trim() || !selectedCategory}
                      sx={{ flex: 1, minWidth: '150px' }}
                      title={!selectedCategory ? "Images are automatically generated when creating categories" : "Generate image using AI"}
                    >
                      {generatingImage ? <CircularProgress size={20} /> : 'Generate Image'}
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* Audio Management */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>Audio</Typography>
                
                {/* Audio state indicators */}
                {!selectedCategory && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Audio will be automatically generated when you create the category.
                  </Alert>
                )}
                {audioPreview && !selectedCategory && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Audio preview generated. Click Save (Update) to persist audio to database.
                  </Alert>
                )}
                {selectedCategory && selectedCategory.soundOptions && selectedCategory.soundOptions.length > 0 && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Audio has been saved to the database for this category.
                  </Alert>
                )}
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(audioPreview || (selectedCategory && selectedCategory.soundOptions && selectedCategory.soundOptions.length > 0)) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={playingAudio === 'preview' ? <Stop /> : <PlayArrow />}
                        onClick={() => handleAudioPreview(
                          audioPreview || (selectedCategory?.soundOptions?.[0]?.soundData ? 
                            `data:${selectedCategory.soundOptions[0].soundContentType || 'audio/mpeg'};base64,${selectedCategory.soundOptions[0].soundData}` : 
                            '')
                        )}
                      >
                        {playingAudio === 'preview' ? 'Stop' : 'Play'} Preview
                      </Button>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<AutoAwesome />}
                      onClick={() => handleGenerateAudio()}
                      disabled={generatingAudio || !formData.audioText?.trim() || !selectedCategory}
                      sx={{ flex: 1, minWidth: '150px' }}
                      title={!selectedCategory ? "Audio is automatically generated when creating categories" : "Generate audio using AI"}
                    >
                      {generatingAudio ? <CircularProgress size={20} /> : 'Generate Audio'}
                    </Button>
                  </Box>
                  {!formData.audioText?.trim() && (
                    <Typography variant="caption" color="warning.main">
                      Audio text is required for audio generation
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Box>
                {!selectedCategory && (
                  <Typography variant="caption" color="text.secondary">
                    Images and audio will be generated automatically when you create the category
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button onClick={handleCloseDialog} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveCategory}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : (selectedCategory ? 'Update' : 'Create Category')}
                </Button>
              </Box>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Snackbar for success messages */}
        {/* Image Preview Modal */}
        <Dialog
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              boxShadow: 'none',
            }
          }}
        >
          <DialogTitle sx={{ color: 'white', pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {selectedImage?.name} - Image Preview
              </Typography>
              <IconButton 
                onClick={() => setImageModalOpen(false)}
                sx={{ color: 'white' }}
              >
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {selectedImage && (
              <Box
                component="img"
                src={selectedImage.url}
                alt={selectedImage.name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Container>
  );
};
